<template>
  <div class="space-y-4">
    <fieldset>
      <legend class="block text-sm font-medium text-gray-700">{{ $t('projects.video_source_label') }}</legend>
      <div class="mt-2 flex flex-wrap gap-4">
        <label v-for="option in sourceOptions" :key="option.value" class="inline-flex items-center text-sm text-gray-700">
          <input type="radio" :name="blockId + '-mode'" :value="option.value" v-model="mode"
                 class="h-4 w-4 text-bpblue border-gray-300 focus:ring-bpblue">
          <span class="ml-2">{{ $t(option.labelKey) }}</span>
        </label>
      </div>
    </fieldset>

    <!-- Vídeo propio: subida multipart directa a S3 (LMS-080) -->
    <div v-if="mode === 'upload'">
      <p v-if="isUploaded" class="mb-2 text-sm text-gray-700">
        {{ $t('projects.video_uploaded') }}<span v-if="uploadedName">: {{ uploadedName }}</span>
      </p>
      <dashboard :uppy="uppy" :props="dashboardProps"></dashboard>
    </div>

    <!-- Enlace externo: el backend lo normaliza a una URL de embed (LMS-083) -->
    <div v-else>
      <label :for="blockId + '-url'" class="block text-sm font-medium text-gray-700">{{ $t('projects.video_url_label') }}</label>
      <div class="mt-1 flex gap-2">
        <input :id="blockId + '-url'" v-model.trim="externalUrl" type="url" inputmode="url"
               :placeholder="$t('projects.video_url_placeholder')" :aria-describedby="blockId + '-url-help'"
               @keyup.enter="resolveEmbed" @blur="resolveEmbed"
               class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:border-blue-300 sm:text-sm">
        <button type="button" v-on:click="resolveEmbed" :disabled="resolving || !externalUrl"
                class="flex-shrink-0 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
          <font-awesome-icon v-if="resolving" class="mr-2 h-4 w-4 animate-spin" icon="spinner"/>
          {{ $t('projects.video_preview') }}
        </button>
      </div>
      <p :id="blockId + '-url-help'" class="mt-2 text-sm text-gray-500">{{ $t('projects.video_url_help') }}</p>
      <p v-if="urlError" class="mt-2 text-sm text-red-600" role="alert">{{ urlError }}</p>
    </div>

    <!-- Previsualización 16:9 responsive -->
    <div v-if="previewUrl" class="video-preview w-full max-w-3xl overflow-hidden rounded-md bg-black">
      <video v-if="mode === 'upload'" :src="previewUrl" controls class="h-full w-full"></video>
      <iframe v-else :src="previewUrl" :title="$t('projects.video_preview')" class="h-full w-full" frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
    </div>
  </div>
</template>

<script>
import { Dashboard } from "@uppy/vue";
import Uppy from "@uppy/core";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import Spanish from "@uppy/locales/lib/es_ES";
import English from "@uppy/locales/lib/en_US";
import Catalan from "@uppy/locales/lib/ca_ES";
import German from "@uppy/locales/lib/de_DE";
import Portuguese from "@uppy/locales/lib/pt_PT";

import NotificationService from "@/services/notification/NotificationService";
import VideoUploadService from "@/services/video/VideoUploadService";
import VideoEmbedService from "@/services/video/VideoEmbedService";
import S3MultipartUploader from "@/services/video/S3MultipartUploader";

const uppyLocales = {es: Spanish, en: English, ca: Catalan, de: German, pt: Portuguese}
const MAX_VIDEO_SIZE = 5 * 1024 * 1024 * 1024 // 5 GB; el backend aplica el límite del plan.

/*
 * Bloque de vídeo (key `video`, LMS-069). `data` es:
 *  - { source: 's3', file_id, playback_url? } para vídeos propios.
 *  - { source: 'youtube' | 'vimeo', url, embed_url? } para enlaces externos.
 * `playback_url` y `embed_url` los añade el backend al devolver el bloque y solo
 * se usan para la previsualización.
 */
export default {
  name: "VideoBlock",
  components: {
    Dashboard
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    blockId: {
      type: String,
      required: true
    },
    lessonUuid: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      mode: this.modelValue.source === 's3' || !this.modelValue.source ? 'upload' : 'embed',
      externalUrl: this.modelValue.url ?? '',
      resolvedUrl: this.modelValue.url ?? '',
      resolving: false,
      urlError: '',
      uploadedName: '',
      sourceOptions: [
        {value: 'upload', labelKey: 'projects.video_source_upload'},
        {value: 'embed', labelKey: 'projects.video_source_embed'},
      ],
    }
  },
  computed: {
    isUploaded() {
      return this.modelValue.source === 's3' && !!this.modelValue.file_id
    },
    previewUrl() {
      if (this.mode === 'upload') {
        return this.isUploaded ? this.modelValue.playback_url : null
      }
      return this.modelValue.source !== 's3' ? this.modelValue.embed_url : null
    },
    dashboardProps() {
      return {
        inline: true,
        height: 260,
        width: '100%',
        proudlyDisplayPoweredByUppy: false,
      }
    }
  },
  created() {
    // Instancia no reactiva: Uppy gestiona su propio estado.
    this.uppy = this.createUppy()
  },
  // En `unmounted` y no antes: el Dashboard hijo retira su plugin en su propio beforeUnmount.
  unmounted() {
    this.uppy.close()
  },
  methods: {
    createUppy() {
      const uppy = new Uppy({
        locale: uppyLocales[this.$i18n.locale] ?? Spanish,
        autoProceed: true,
        restrictions: {
          maxFileSize: MAX_VIDEO_SIZE,
          maxNumberOfFiles: 1,
          allowedFileTypes: ['video/*'],
        },
      })
      const uploader = new S3MultipartUploader(VideoUploadService)

      uppy.addUploader(async (fileIDs) => {
        for (const fileID of fileIDs) {
          await this.uploadFile(uppy, uploader, fileID)
        }
      })

      return uppy
    },
    async uploadFile(uppy, uploader, fileID) {
      const file = uppy.getFile(fileID)
      uppy.emit('upload-start', [file])

      try {
        const result = await uploader.upload(file.data, {
          onProgress: (bytesUploaded, bytesTotal) => {
            uppy.emit('upload-progress', uppy.getFile(fileID), {uploader: this, bytesUploaded, bytesTotal})
          }
        })
        uppy.emit('upload-success', uppy.getFile(fileID), {status: 200, body: result})

        this.uploadedName = file.name
        this.$emit('update:modelValue', {
          source: 's3',
          file_id: result.file_id,
          playback_url: result.playback_url ?? null,
        })
        NotificationService.showNotification(this.$t('projects.video_uploaded'), "success")
      } catch (error) {
        uppy.emit('upload-error', uppy.getFile(fileID), error)
        NotificationService.showApiError(error, this.$t('projects.video_upload_error'))
      }
    },
    resolveEmbed() {
      if (!this.externalUrl || this.resolving || this.externalUrl === this.resolvedUrl) return

      this.resolving = true
      this.urlError = ''
      VideoEmbedService.resolve(this.externalUrl)
          .then((response) => {
            const embed = response.data?.data ?? response.data
            this.resolvedUrl = this.externalUrl
            this.$emit('update:modelValue', {
              source: embed.provider,
              url: this.externalUrl,
              embed_url: embed.embed_url,
            })
          })
          .catch((error) => {
            this.urlError = error?.response?.status === 422
                ? this.$t('projects.video_url_unsupported')
                : this.$t('projects.video_url_error')
            NotificationService.showApiError(error, this.urlError)
          })
          .finally(() => {
            this.resolving = false
          })
    },
  }
}
</script>

<style scoped>
.video-preview {
  aspect-ratio: 16 / 9;
}
</style>
